import {ConfirmationDialog} from '@primer/react'
import {useCallback} from 'react'

import {unblockUserFromOrganization} from '../../mutations/unblock-user-from-organization-mutation'

type UnblockUserFromOrgDialogBaseProps = {
  organization: {login: string}
  contentAuthor: {login: string}
  onClose: () => void
}

type UnblockUserFromOrgDialogWithControllerProps = UnblockUserFromOrgDialogBaseProps & {
  organization: {login: string; id: string}
  contentAuthor: {login: string; id: string}
  onUnblock?: undefined
}

type UnblockUserFromOrgDialogWithCallbackProps = UnblockUserFromOrgDialogBaseProps & {
  /**
   * Callback function to handle the unblock action instead of the default controller mutation.
   */
  onUnblock: (organizationLogin: string, userLogin: string) => void
}

export type UnblockUserFromOrgDialogProps =
  | UnblockUserFromOrgDialogWithControllerProps
  | UnblockUserFromOrgDialogWithCallbackProps

export const UnblockUserFromOrgDialog = (props: UnblockUserFromOrgDialogProps) => {
  return props.onUnblock ? (
    <UnblockUserFromOrgDialogWithCallback {...props} />
  ) : (
    <UnblockUserFromOrgDialogWithController {...props} />
  )
}

const UnblockUserFromOrgDialogWithController = ({
  contentAuthor,
  organization,
  onClose,
}: UnblockUserFromOrgDialogWithControllerProps) => {
  const onDialogClose = useCallback(
    (gesture: 'confirm' | 'cancel' | 'close-button' | 'escape') => {
      if (gesture === 'confirm') {
        unblockUserFromOrganization({
          input: {unblockedUserId: contentAuthor.id, organizationId: organization.id},
        })
      }
      onClose()
    },
    [onClose, contentAuthor.id, organization.id],
  )

  return (
    <ConfirmationDialog
      title={`Unblock ${contentAuthor.login} from ${organization.login}`}
      confirmButtonContent={'Unblock user'}
      confirmButtonType="danger"
      onClose={onDialogClose}
    >
      Are you sure you want to unblock <strong>{contentAuthor.login}</strong> from <strong>{organization.login}</strong>
      ?
    </ConfirmationDialog>
  )
}

const UnblockUserFromOrgDialogWithCallback = ({
  contentAuthor,
  organization,
  onClose,
  onUnblock,
}: UnblockUserFromOrgDialogWithCallbackProps) => {
  const onDialogClose = useCallback(
    (gesture: 'confirm' | 'cancel' | 'close-button' | 'escape') => {
      if (gesture === 'confirm') {
        onUnblock(organization.login, contentAuthor.login)
      }
      onClose()
    },
    [onClose, onUnblock, organization.login, contentAuthor.login],
  )

  return (
    <ConfirmationDialog
      title={`Unblock ${contentAuthor.login} from ${organization.login}`}
      confirmButtonContent={'Unblock user'}
      confirmButtonType="danger"
      onClose={onDialogClose}
    >
      Are you sure you want to unblock <strong>{contentAuthor.login}</strong> from <strong>{organization.login}</strong>
      ?
    </ConfirmationDialog>
  )
}
